const INSTALLED_KEY = "bb_pwa_installed";
const DISMISSED_KEY = "bb_pwa_dismissed";

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

class PwaInstallPrompt {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private hasShownThisSession = false;

  init(): void {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
    });

    window.addEventListener("appinstalled", () => {
      this.deferredPrompt = null;
      try {
        localStorage.setItem(INSTALLED_KEY, "1");
      } catch { /* no-op */ }
    });
  }

  /** Returns true if we should show the install button. */
  shouldShow(): boolean {
    if (this.deferredPrompt == null) return false;
    if (this.hasShownThisSession) return false;
    try {
      if (localStorage.getItem(INSTALLED_KEY) === "1") return false;
      if (localStorage.getItem(DISMISSED_KEY) === "1") return false;
    } catch { /* no-op */ }
    return true;
  }

  /** Mark that we've shown the prompt this session (to avoid repeat). */
  markShown(): void {
    this.hasShownThisSession = true;
  }

  /** Trigger the native install prompt. */
  async prompt(): Promise<boolean> {
    if (this.deferredPrompt == null) return false;
    try {
      await this.deferredPrompt.prompt();
      const result = await this.deferredPrompt.userChoice;
      this.deferredPrompt = null;
      if (result.outcome === "accepted") {
        try { localStorage.setItem(INSTALLED_KEY, "1"); } catch { /* no-op */ }
        return true;
      } else {
        try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* no-op */ }
        return false;
      }
    } catch {
      return false;
    }
  }

  isAvailable(): boolean {
    return this.deferredPrompt != null;
  }
}

export const pwaInstallPrompt = new PwaInstallPrompt();
